/**
 * Abbinare a mano un'azienda di YumaOS a un partner: per chi l'ha portata prima di avere l'accesso (Energit e K&A a
 * Romano Paolicelli). Se l'azienda è già di un altro partner, sostituirlo vuole il motivo.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import { chiavePartitaIva } from '@shared/partner/compensoPartner';
import type { AbbinamentoDoc, PartnerDoc } from '@shared/partner/tipi';
import {
  abbinaAMano, leggiAziende, type AziendaDellaPiattaforma, type ConId,
} from '../../services/partnerConsoleService';
import { oggi } from './formati';

interface Props {
  aperto: boolean;
  partner: Array<ConId<PartnerDoc>>;
  abbinamenti: Array<ConId<AbbinamentoDoc>>;
  onChiudi: () => void;
  onSalvato: () => void;
  onErrore: (messaggio: string) => void;
}

const STATI_CHE_VALGONO = new Set(['registrata', 'da_decidere', 'attiva']);

export const DialogoAbbina: React.FC<Props> = ({ aperto, partner, abbinamenti, onChiudi, onSalvato, onErrore }) => {
  const { t } = useTranslation('partner');
  const [aziende, setAziende] = useState<AziendaDellaPiattaforma[]>([]);
  const [azienda, setAzienda] = useState<AziendaDellaPiattaforma | null>(null);
  const [partnerId, setPartnerId] = useState('');
  const [contareDal, setContareDal] = useState(oggi());
  const [sostituisci, setSostituisci] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [lavora, setLavora] = useState(false);

  useEffect(() => {
    if (!aperto) return;
    setAzienda(null); setPartnerId(''); setContareDal(oggi()); setSostituisci(false); setMotivo('');
    leggiAziende().then(setAziende).catch((e) => onErrore(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aperto]);

  /** Se l'azienda scelta è già di un partner (stessa partita IVA), chi. */
  const giaDi = useMemo(() => {
    if (!azienda?.partita_iva) return null;
    const chiave = chiavePartitaIva(azienda.partita_iva, 'IT');
    const a = abbinamenti.find((x) => x.id === chiave && STATI_CHE_VALGONO.has(x.stato));
    return a ? partner.find((p) => p.id === a.partner_id) ?? null : null;
  }, [azienda, abbinamenti, partner]);

  const conflitto = !!giaDi && giaDi.id !== partnerId && !!partnerId;
  const completo = !!azienda && !!partnerId && !!contareDal && (!conflitto || (sostituisci && motivo.trim().length > 2));

  const salva = async () => {
    if (!azienda) return;
    setLavora(true);
    try {
      await abbinaAMano({
        tenant_id: azienda.id,
        partner_id: partnerId,
        contare_dal: contareDal,
        ...(conflitto ? { sostituisci: true, motivo: motivo.trim() } : {}),
      });
      onSalvato();
      onChiudi();
    } catch (e) {
      onErrore(e instanceof Error ? e.message : String(e));
    } finally {
      setLavora(false);
    }
  };

  return (
    <Dialog open={aperto} onClose={lavora ? undefined : onChiudi} fullWidth maxWidth="sm">
      <DialogTitle>{t('dialogo_abbina.titolo')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: tokens.color.ash, mb: 2 }}>{t('dialogo_abbina.aiuto')}</Typography>
        <Stack spacing={2}>
          <Autocomplete
            options={aziende}
            value={azienda}
            onChange={(_e, v) => setAzienda(v)}
            getOptionLabel={(a) => (a.partita_iva ? `${a.nome} · ${a.partita_iva}` : a.nome)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} label={t('dialogo_abbina.azienda')} size="small" />}
          />
          <TextField select label={t('dialogo_abbina.partner')} value={partnerId} onChange={(e) => setPartnerId(e.target.value)} size="small" fullWidth>
            {partner.filter((p) => p.stato !== 'cessato').map((p) => <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>)}
          </TextField>
          <TextField
            type="date"
            label={t('dialogo_abbina.contare_dal')}
            value={contareDal}
            onChange={(e) => setContareDal(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          {conflitto && (
            <>
              <Alert severity="warning">{t('dialogo_abbina.gia_di', { nome: giaDi?.nome ?? '' })}</Alert>
              <FormControlLabel
                control={<Checkbox checked={sostituisci} onChange={(e) => setSostituisci(e.target.checked)} />}
                label={t('dialogo_abbina.sostituisci')}
              />
              {sostituisci && (
                <TextField label={t('dialogo_abbina.motivo')} value={motivo} onChange={(e) => setMotivo(e.target.value)} size="small" multiline minRows={2} />
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onChiudi} disabled={lavora} sx={{ textTransform: 'none' }}>{t('azioni.annulla')}</Button>
        <Button variant="contained" onClick={salva} disabled={!completo || lavora} startIcon={lavora ? <CircularProgress size={14} /> : undefined} sx={{ textTransform: 'none' }}>
          {t('azioni.abbina')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
